import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import {
    Alert,
    Collapse,
    Grid,
    InputAdornment,
    styled,
    Typography,
    useTheme,
} from "@mui/material";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { AcxAgentService } from "services/AcxAgentService";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import AcxTextField from "components/UI/AcxTextField";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import AcxEmptyState from "components/UI/AcxEmptyStateIndicator/AcxEmptyState";
import AgentStore from "../Stores/AgentStore";
import { useStore } from "utils/useStore";
import { SidebarChat } from "./SidebarChat";
import { useNavigate } from "react-router-dom";
import { sxClsx } from "utils/styleClsx";

const PAGE_SIZE = 50;

export interface AgentFullscreenSidebarProps {
    canAccessKboSettings?: boolean;
}

export const AgentFullscreenSidebar = observer(
    ({ canAccessKboSettings = false }: AgentFullscreenSidebarProps) => {
        const [loading, setLoading] = useState(false);
        const [loadingMore, setLoadingMore] = useState(false);
        const [error, setError] = useState<string | null>(null);
        const [searchQuery, setSearchQuery] = useState("");
        const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

        const [pageNumber, setPageNumber] = useState(1);
        const [hasMore, setHasMore] = useState(true);

        const observerRef = useRef<IntersectionObserver | null>(null);
        const loadMoreRef = useRef<HTMLDivElement | null>(null);
        const currentChatCountRef = useRef(0);

        const agentStore = useStore(AgentStore);

        // Only recreate loadChats when debouncedSearchQuery changes
        const loadChats = useCallback(
            async (
                isInitial: boolean = true,
                searchOverride?: string,
                page?: number,
            ) => {
                const agentService = new AcxAgentService();

                const pageToLoad = page ?? pageNumber;

                if (isInitial) {
                    setLoading(true);
                } else {
                    setLoadingMore(true);
                }
                setError(null);

                try {
                    const currentSearch =
                        searchOverride !== undefined
                            ? searchOverride
                            : debouncedSearchQuery;

                    // Build search string based on filter and search query
                    let searchString: string | undefined =
                        currentSearch || undefined;

                    // Fetch the users chats
                    try {
                        const response = await agentService.getMyChats({
                            page: pageToLoad,
                            pageSize: PAGE_SIZE,
                            searchString,
                        });
                        if (isInitial) {
                            // If this is our first load, set the visible chat IDs
                            agentStore.setVisibleChatIds(
                                response.chats.map((c) => c.id),
                            );
                            // If we don't already have a chat store, addChat will create one
                            response.chats.forEach((chat) => {
                                if (!agentStore.hasChat(chat.id)) {
                                    agentStore.addChat(chat);
                                }
                            });
                        } else {
                            // If this is not our first load, we should merge the new chats with the existing ones
                            // This section is mainly useful for when you make a new chat so it shows in the sidebar immediately
                            const previousIds = agentStore.visibleChatIds;
                            const newIds = response.chats.map((c) => c.id);
                            // Create stores for new chat ids that are visible
                            response.chats.forEach((chat) => {
                                if (!agentStore.hasChat(chat.id)) {
                                    agentStore.addChat(chat);
                                }
                            });
                            agentStore.setVisibleChatIds([
                                ...previousIds,
                                ...newIds,
                            ]);
                        }
                        setHasMore(
                            pageToLoad * PAGE_SIZE < response.totalCount,
                        );
                        setPageNumber((prev) => prev + 1);
                    } catch (error) {
                        console.error("Error loading chat sessions:", error);
                    }
                } catch (err) {
                    setError(
                        err instanceof Error
                            ? err.message
                            : "Failed to load chat sessions",
                    );
                } finally {
                    setLoading(false);
                    setLoadingMore(false);
                }
            },
            [debouncedSearchQuery, agentStore, pageNumber],
        );

        // Create debounced search handler
        const debouncedSearch = useMemo(
            () =>
                debounce((searchValue: string) => {
                    setDebouncedSearchQuery(searchValue);
                }, 1000),
            [],
        );

        // Update debounced search when search query changes
        useEffect(() => {
            debouncedSearch(searchQuery);
            return () => {
                debouncedSearch.cancel();
            };
        }, [searchQuery, debouncedSearch]);

        // Reset chats and pagination when search/filter changes
        useEffect(() => {
            agentStore.setVisibleChatIds([]);
            currentChatCountRef.current = 0;
            setHasMore(true);
            setPageNumber(1);
            loadChats(true, debouncedSearchQuery, 1);
            // eslint wants loadChats but that causes infinite rerenders
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [debouncedSearchQuery, agentStore]);

        // Only rerun observer effect when hasMore or loadMoreRef changes
        useEffect(() => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
            observerRef.current = new IntersectionObserver(
                (entries) => {
                    const lastEntry = entries[0];
                    if (
                        lastEntry.isIntersecting &&
                        hasMore &&
                        !loadingMore &&
                        !loading
                    ) {
                        loadChats(false, debouncedSearchQuery, pageNumber);
                    }
                },
                {
                    threshold: 0.1,
                },
            );
            if (loadMoreRef.current) {
                observerRef.current.observe(loadMoreRef.current);
            }
            return () => {
                if (observerRef.current) {
                    observerRef.current.disconnect();
                }
            };
            // eslint wants loadChats but that causes infinite rerenders
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [hasMore, pageNumber, debouncedSearchQuery, loading, loadingMore]);

        const abortControllerRef = useRef(new AbortController());
        const [chatsExpanded, setChatsExpanded] = useState(true);
        const categorizedChats = agentStore.categorizedChats;

        // Flattened in the order the categorized sections used to render, so
        // collapsing them into one list leaves the sequence untouched.
        const orderedChats = useMemo(
            () => [
                ...categorizedChats.kboFlow,
                ...categorizedChats.recent,
                ...categorizedChats.last30,
                ...Object.keys(categorizedChats.months).flatMap(
                    (month) => categorizedChats.months[month],
                ),
            ],
            [categorizedChats],
        );

        return (
            <Grid
                sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Grid
                    sx={{
                        flex: 1,
                        overflow: "auto",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Grid sx={{ p: 1, paddingRight: 2 }}>
                        <AcxTextField
                            id="kbo-fullscreen-search"
                            type="text"
                            fullWidth
                            label=""
                            placeholder="Search chats..."
                            helperText=""
                            error={false}
                            required={false}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            other={{
                                value: searchQuery,
                                InputProps: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                },
                                sx: {
                                    "& .MuiInputBase-input": {
                                        paddingY: 1,
                                        paddingLeft: 0,
                                        paddingRight: 1,
                                    },
                                },
                            }}
                        />
                    </Grid>
                    {error && (
                        <Alert
                            severity="error"
                            onClose={() => setError(null)}
                            sx={{ m: 2 }}
                        >
                            {error}
                        </Alert>
                    )}
                    {loading ? (
                        <Grid
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                p: 4,
                            }}
                        >
                            <AcxLoadingIndicator
                                size={25}
                                alternate="PuffLoader"
                            />
                        </Grid>
                    ) : agentStore.visibleChatIds.length === 0 ? (
                        <AcxEmptyState
                            size="small"
                            variant="empty"
                            overrideTitle={
                                searchQuery
                                    ? "No chats found matching your search"
                                    : "No chat sessions found"
                            }
                            overrideSubTitle={
                                searchQuery
                                    ? "Try adjusting your search"
                                    : "No chat sessions found"
                            }
                            height={300}
                        />
                    ) : (
                        <Grid
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                                paddingTop: 1.5,
                            }}
                        >
                            <Grid
                                role="button"
                                tabIndex={0}
                                aria-expanded={chatsExpanded}
                                id="kbo-fullscreen-chats-toggle"
                                onClick={() =>
                                    setChatsExpanded((expanded) => !expanded)
                                }
                                onKeyDown={(event) => {
                                    if (
                                        event.key !== "Enter" &&
                                        event.key !== " "
                                    ) {
                                        return;
                                    }
                                    event.preventDefault();
                                    setChatsExpanded((expanded) => !expanded);
                                }}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.25,
                                    cursor: "pointer",
                                    userSelect: "none",
                                }}
                            >
                                <StyledListHeader>CHATS</StyledListHeader>
                                <KeyboardArrowDownRoundedIcon
                                    sx={(theme) => ({
                                        fontSize: 18,
                                        color: theme.palette.gray[400],
                                        transform: chatsExpanded
                                            ? "none"
                                            : "rotate(-90deg)",
                                        transition: "transform 0.15s",
                                    })}
                                />
                            </Grid>
                            <Collapse in={chatsExpanded}>
                                <Grid
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 0.5,
                                    }}
                                >
                                    {orderedChats.map((chatStore) => (
                                        <SidebarChat
                                            key={chatStore.chat.id}
                                            selected={
                                                agentStore.currentChatId ===
                                                chatStore.chat.id
                                            }
                                            chatStore={chatStore}
                                            abortControllerRef={
                                                abortControllerRef
                                            }
                                        />
                                    ))}
                                    {/* Infinite scroll trigger */}
                                    {hasMore && (
                                        <Grid
                                            ref={loadMoreRef}
                                            sx={{ p: 2, textAlign: "center" }}
                                        >
                                            {loadingMore ? (
                                                <AcxLoadingIndicator
                                                    size={24}
                                                    alternate="PuffLoader"
                                                />
                                            ) : (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Scroll to load more...
                                                </Typography>
                                            )}
                                        </Grid>
                                    )}
                                </Grid>
                            </Collapse>
                        </Grid>
                    )}
                </Grid>
                {canAccessKboSettings && <SidebarSettingsItem />}
            </Grid>
        );
    },
);

const SidebarSettingsItem = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [hover, setHover] = useState(false);

    return (
        <Grid
            sx={{
                flexShrink: 0,
                borderTop: `1px solid ${theme.palette.gray[200]}`,
                paddingTop: 1,
                marginTop: 1,
                marginLeft: -2,
            }}
        >
            <Grid
                id="kbo-settings"
                // Settings layout defaults to first visible nav item
                onClick={() => navigate("/app/kbo/settings")}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                sx={sxClsx(
                    {
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1,
                        paddingY: "6px",
                        px: 1,
                        marginLeft: 2,
                        marginRight: 2,
                        borderRadius: 2,
                        cursor: "pointer",
                        transition: "background-color 0.1s",
                    },
                    hover && {
                        backgroundColor: theme.palette.gray[200],
                    },
                )}
            >
                <SettingsRoundedIcon
                    sx={{
                        fontSize: 20,
                        color: theme.palette.gray[400],
                    }}
                />
                <Typography
                    sx={{
                        color: theme.palette.neutral[600],
                        fontSize: "14px",
                        fontWeight: 500,
                        lineHeight: "20px",
                    }}
                >
                    Settings
                </Typography>
            </Grid>
        </Grid>
    );
};

const StyledListHeader = styled(Typography)(({ theme }) => ({
    color: theme.palette.gray[400],
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: "20px",
    paddingLeft: theme.spacing(1),
}));
